import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, type NavigateFunction } from "react-router-dom";
import type { AuthMe } from "@icraftx/shared/v1";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { PendingBar } from "@/components/ui/pending-indicator";
import {
  fetchAuthStatus,
  loginDev,
  loginWithGoogle,
  type AuthStatus,
} from "@/shared/api/auth";

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (cfg: {
            client_id: string;
            callback: (res: { credential: string }) => void;
          }) => void;
          renderButton: (
            el: HTMLElement,
            cfg: { theme?: string; size?: string; width?: number },
          ) => void;
          cancel?: () => void;
        };
      };
    };
  }
}

/** GIS warns if initialize() runs more than once per page lifetime. */
let gisClientIdInitialized: string | null = null;

function loadGis(): Promise<void> {
  if (window.google?.accounts?.id) return Promise.resolve();
  return new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      'script[data-google-gis="1"]',
    );
    if (existing) {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", () =>
        reject(new Error("GIS load failed")),
      );
      return;
    }
    const s = document.createElement("script");
    s.src = "https://accounts.google.com/gsi/client";
    s.async = true;
    s.dataset.googleGis = "1";
    s.onload = () => resolve();
    s.onerror = () => reject(new Error("GIS load failed"));
    document.head.appendChild(s);
  });
}

function routeAfterLogin(me: AuthMe, navigate: NavigateFunction) {
  if (me.user.isSuperadmin && me.companies.length === 0) {
    navigate("/superadmin", { replace: true });
    return;
  }
  // Superadmins always land on the picker (Platform entry stays out of company UI).
  if (me.companies.length === 1 && !me.user.isSuperadmin) {
    const slug = me.companies.map((c) => c.slug)[0];
    if (slug) {
      navigate(`/c/${slug}`, { replace: true });
      return;
    }
  }
  navigate("/select-company", { replace: true });
}

export function LoginPage() {
  const navigate = useNavigate();
  const btnRef = useRef<HTMLDivElement>(null);
  const callbackRef = useRef<(cred: string) => Promise<void>>(async () => {});
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  callbackRef.current = async (credential: string) => {
    setBusy(true);
    setError(null);
    try {
      routeAfterLogin(await loginWithGoogle(credential), navigate);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Sign-in failed");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    fetchAuthStatus()
      .then((s) => {
        if (!cancelled) setStatus(s);
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "API unreachable");
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const clientId = status?.googleClientId;
    if (!status?.googleConfigured || !clientId || !btnRef.current) {
      return;
    }
    let cancelled = false;
    loadGis()
      .then(() => {
        if (cancelled || !btnRef.current || !window.google) return;
        if (gisClientIdInitialized !== clientId) {
          window.google.accounts.id.initialize({
            client_id: clientId,
            callback: (res) => {
              void callbackRef.current(res.credential);
            },
          });
          gisClientIdInitialized = clientId;
        }
        // Always re-render the button into this mount's container (Strict Mode safe).
        btnRef.current.replaceChildren();
        window.google.accounts.id.renderButton(btnRef.current, {
          theme: "outline",
          size: "large",
          width: 320,
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Google script failed");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  async function onDevLogin() {
    if (!status) return;
    setBusy(true);
    setError(null);
    try {
      routeAfterLogin(await loginDev(status.superadminEmail), navigate);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Dev login failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto flex min-h-[min(100dvh,52rem)] max-w-md flex-col justify-center px-4 py-12 animate-[softRise_420ms_ease-out]">
      <p className="m-0 font-[family-name:var(--font-display)] text-sm font-bold tracking-[0.16em] text-[var(--color-primary)] uppercase">
        iCraftX
      </p>
      <h1 className="m-0 mt-3 font-[family-name:var(--font-display)] text-4xl font-semibold tracking-tight text-[var(--color-foreground)]">
        Login
      </h1>
      <p className="mt-2 max-w-sm text-sm leading-relaxed text-[var(--color-muted)]">
        Sign in with Google. Passcode is suggested later, not required.
      </p>

      {status?.googleConfigured ? (
        <div
          className={`mt-8 flex justify-center ${busy ? "pointer-events-none opacity-60" : ""}`}
          ref={btnRef}
          aria-busy={busy || undefined}
        />
      ) : status == null ? (
        error ? null : (
          <PendingBar className="mt-8" label="Loading sign-in…" />
        )
      ) : (
        <p className="mt-8 text-sm text-[var(--color-muted)]">
          Google is not configured on the API yet.
        </p>
      )}

      {status?.devBypass && (
        <Button
          className="mt-6 w-full"
          type="button"
          variant="outline"
          pending={busy}
          pendingLabel="Signing in…"
          disabled={!status.sessionConfigured}
          onClick={() => void onDevLogin()}
        >
          Dev login as {status.superadminEmail}
        </Button>
      )}

      {busy && status?.googleConfigured ? (
        <PendingBar className="mt-4" label="Signing in…" />
      ) : null}
      {error && (
        <Alert variant="destructive" className="mt-4">
          {error}
        </Alert>
      )}

      <p className="mt-10 text-center text-sm text-[var(--color-muted)]">
        <Link to="/" className="no-underline hover:underline">
          Back home
        </Link>
      </p>
    </div>
  );
}
